import React, { useState, useCallback } from "react";

import Button from "../../components/design-system/Button";
import { cn } from "@faire/prototype-playground/lib/utils";

interface Category {
  id: string;
  label: string;
  children?: Category[];
}

const CATEGORIES: Category[] = [
  {
    id: "bath-body",
    label: "Bath & body",
    children: [
      { id: "bath-body-soaps", label: "Soaps" },
      { id: "bath-body-lotions", label: "Lotions" },
      { id: "bath-body-bath-bombs", label: "Bath bombs" },
    ],
  },
  {
    id: "books",
    label: "Books",
    children: [
      { id: "books-fiction", label: "Fiction" },
      { id: "books-nonfiction", label: "Non-fiction" },
      { id: "books-children", label: "Children's" },
    ],
  },
  {
    id: "fitness-yoga",
    label: "Fitness & yoga",
    children: [
      { id: "fitness-mats", label: "Mats" },
      { id: "fitness-accessories", label: "Accessories" },
      { id: "fitness-apparel", label: "Apparel" },
    ],
  },
  {
    id: "food-drink",
    label: "Food & drink",
    children: [
      { id: "food-snacks", label: "Snacks" },
      { id: "food-beverages", label: "Beverages" },
      { id: "food-chocolate", label: "Chocolate" },
    ],
  },
  {
    id: "fragrance",
    label: "Fragrance",
    children: [
      { id: "fragrance-perfume", label: "Perfume" },
      { id: "fragrance-candles", label: "Candles" },
      { id: "fragrance-incense", label: "Incense" },
    ],
  },
  {
    id: "haircare",
    label: "Haircare",
    children: [
      { id: "haircare-shampoo", label: "Shampoo" },
      { id: "haircare-conditioner", label: "Conditioner" },
      { id: "haircare-styling", label: "Styling" },
    ],
  },
  {
    id: "jewelry",
    label: "Jewelry",
    children: [
      { id: "jewelry-necklaces", label: "Necklaces" },
      { id: "jewelry-earrings", label: "Earrings" },
      { id: "jewelry-bracelets", label: "Bracelets" },
    ],
  },
  {
    id: "kids-baby",
    label: "Kids & baby",
    children: [
      { id: "kids-toys", label: "Toys" },
      { id: "kids-clothing", label: "Clothing" },
      { id: "kids-accessories", label: "Accessories" },
    ],
  },
  {
    id: "makeup",
    label: "Makeup",
    children: [
      { id: "makeup-eyes", label: "Eyes" },
      { id: "makeup-face", label: "Face" },
      { id: "makeup-lips", label: "Lips" },
    ],
  },
  {
    id: "mens-apparel",
    label: "Men's apparel",
    children: [
      { id: "mens-shirts", label: "Shirts" },
      { id: "mens-pants", label: "Pants" },
      { id: "mens-outerwear", label: "Outerwear" },
    ],
  },
  {
    id: "personal-care",
    label: "Personal care",
    children: [
      { id: "personal-oral", label: "Oral care" },
      { id: "personal-deodorant", label: "Deodorant" },
      { id: "personal-shaving", label: "Shaving" },
    ],
  },
  {
    id: "skincare",
    label: "Skincare",
    children: [
      { id: "skincare-moisturizers", label: "Moisturizers" },
      { id: "skincare-serums", label: "Serums" },
      { id: "skincare-cleansers", label: "Cleansers" },
    ],
  },
  {
    id: "pets",
    label: "Pets",
    children: [
      { id: "pets-treats", label: "Treats" },
      { id: "pets-toys", label: "Toys" },
      { id: "pets-grooming", label: "Grooming" },
    ],
  },
  {
    id: "womens-apparel",
    label: "Women's apparel",
    children: [
      { id: "womens-tops", label: "Tops" },
      { id: "womens-dresses", label: "Dresses" },
      { id: "womens-bottoms", label: "Bottoms" },
    ],
  },
];

const MAX_SELECTIONS = 10;

function ChevronDown({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("ml-1 transition-transform duration-200", expanded ? "rotate-180" : "")}
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CategoryChip({
  label,
  selected,
  expanded,
  hasChildren,
  onClick,
  disabled,
}: {
  label: string;
  selected: boolean;
  expanded?: boolean;
  hasChildren?: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  const baseClasses =
    "inline-flex items-center justify-center px-3 py-2 rounded text-sm tracking-[0.15px] transition-all duration-200 cursor-pointer select-none";

  let stateClasses: string;
  if (selected || expanded) {
    stateClasses = "bg-[#e8eeff] border border-transparent text-[#333]";
  } else {
    stateClasses =
      "bg-fs-surface-primary border border-[#dfe0e1] text-[#333] hover:border-[#999]";
  }

  if (disabled && !selected) {
    stateClasses += " opacity-50 cursor-not-allowed";
  }

  return (
    <button
      type="button"
      onClick={disabled && !selected ? undefined : onClick}
      className={cn(baseClasses, stateClasses)}
      style={{
        fontFamily: "'Graphik', sans-serif",
        lineHeight: "20px",
      }}
    >
      {label}
      {hasChildren && <ChevronDown expanded={!!expanded} />}
    </button>
  );
}

function SubcategoryChip({
  label,
  selected,
  onClick,
  disabled,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  const baseClasses =
    "inline-flex items-center justify-center px-3 py-2 rounded text-sm tracking-[0.15px] transition-all duration-200 cursor-pointer select-none";

  let stateClasses: string;
  if (selected) {
    stateClasses = "bg-[#a2b8ff] border border-transparent text-[#333]";
  } else {
    stateClasses =
      "bg-fs-surface-primary border border-dashed border-[#bbb] text-[#555] hover:border-[#999]";
  }

  if (disabled && !selected) {
    stateClasses += " opacity-50 cursor-not-allowed";
  }

  return (
    <button
      type="button"
      onClick={disabled && !selected ? undefined : onClick}
      className={cn(baseClasses, stateClasses)}
      style={{
        fontFamily: "'Graphik', sans-serif",
        lineHeight: "20px",
      }}
    >
      {label}
    </button>
  );
}

export default function Variant1() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedParents, setExpandedParents] = useState<Set<string>>(
    new Set()
  );

  const selectionCount = selectedIds.size;
  const atLimit = selectionCount >= MAX_SELECTIONS;

  const toggleCategory = useCallback(
    (category: Category) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);

        if (category.children) {
          if (expandedParents.has(category.id)) {
            // Collapsing: remove parent and all children
            setExpandedParents((ep) => {
              const n = new Set(ep);
              n.delete(category.id);
              return n;
            });
            next.delete(category.id);
            category.children.forEach((c) => next.delete(c.id));
          } else {
            // Expanding: show children, select the parent
            setExpandedParents((ep) => new Set(ep).add(category.id));
            if (!atLimit) {
              next.add(category.id);
            }
          }
        } else {
          if (next.has(category.id)) {
            next.delete(category.id);
          } else if (!atLimit) {
            next.add(category.id);
          }
        }

        return next;
      });
    },
    [expandedParents, atLimit]
  );

  const toggleSubcategory = useCallback(
    (child: Category) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(child.id)) {
          next.delete(child.id);
        } else if (!atLimit) {
          next.add(child.id);
        }
        return next;
      });
    },
    [atLimit]
  );

  return (
    <div className="flex min-h-screen items-start justify-center bg-[#f5f5f5] py-8">
      <div
        className="flex flex-col overflow-hidden rounded-3xl bg-fs-surface-primary shadow-lg"
        style={{ width: 375, height: 812 }}
      >
        {/* Nav bar */}
        <div className="relative flex shrink-0 items-center justify-center px-6 py-4">
          <button
            type="button"
            className="absolute left-2 p-3.5 text-[#333]"
            aria-label="Back"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3L5 8L10 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span
            className="text-base tracking-[0.35em] text-[#333]"
            style={{ fontFamily: "'Graphik', sans-serif", fontWeight: 400 }}
          >
            F A I R E
          </span>
        </div>

        {/* Progress bar */}
        <div className="flex w-full shrink-0">
          <div className="h-1 w-1/4 bg-[#333]" />
          <div className="h-1 flex-1 bg-[#f7f7f7]" />
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-6 pt-8">
          <h1
            className="mb-4 text-[30px] leading-[38px] text-[#333]"
            style={{ fontFamily: "'Nantes', serif" }}
          >
            What product categories are you interested in?
          </h1>
          <p
            className="mb-8 text-sm leading-5 tracking-[0.15px] text-[#333]"
            style={{ fontFamily: "'Graphik', sans-serif" }}
          >
            Select up to {MAX_SELECTIONS}—we'll use this to personalize your
            experience.
          </p>

          {/* Selection counter */}
          {selectionCount > 0 && (
            <div
              className="mb-4 text-xs tracking-[0.15px] text-[#757575]"
              style={{ fontFamily: "'Graphik', sans-serif" }}
            >
              {selectionCount} of {MAX_SELECTIONS} selected
            </div>
          )}

          {/* Category chips */}
          <div className="flex flex-wrap gap-2 pb-6">
            {CATEGORIES.map((cat) => {
              const isExpanded = expandedParents.has(cat.id);
              const isSelected = selectedIds.has(cat.id);

              return (
                <React.Fragment key={cat.id}>
                  <CategoryChip
                    label={cat.label}
                    selected={isSelected}
                    expanded={isExpanded}
                    hasChildren={!!cat.children}
                    onClick={() => toggleCategory(cat)}
                    disabled={atLimit}
                  />

                  {isExpanded &&
                    cat.children?.map((child) => (
                      <SubcategoryChip
                        key={child.id}
                        label={child.label}
                        selected={selectedIds.has(child.id)}
                        onClick={() => toggleSubcategory(child)}
                        disabled={atLimit}
                      />
                    ))}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Sticky bottom button */}
        <div className="shrink-0 border-t border-[#f0f0f0] bg-fs-surface-primary px-6 py-4">
          <Button
            variant="primary"
            disabled={selectionCount === 0}
            className="w-full"
            onClick={() => {
              alert(
                `Selected: ${Array.from(selectedIds).join(", ")}`
              );
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
